import { useQueryClient } from '@tanstack/react-query'
// import { useFlag } from '@unleash/proxy-client-react'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useTradeRoutesConditions } from '@pages/Trade/Trade.routes'

import { useTransactionWebSocketMessage } from '@features/websocket'

import { useSessionEntity } from '@context/EntityContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSnackbar } from '@context/SnackbarContext'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingNav } from '@hooks/useFundingNav'

import type { WebSocketMessage } from '../core/types'
import { refreshTransactionEventData } from './transactionDataRefresh'
import { buildTransactionSnackbar, normalizeTransactionWebSocketEvent } from './transactionEvent'

const useCanAccessWebTrading = (): boolean => {
  // const isTradingPoCActive = useFlag('TradingPoC')
  const isTradingPoCActive = true

  const entity = useSessionEntity()
  const { isDefaultCFDProductType } = useProductReadContext()
  const { canAccessTrade } = useTradeRoutesConditions()

  return (
    isTradingPoCActive &&
    entity === TickmillCompaniesEnum.TICKMILL_SC &&
    isDefaultCFDProductType() &&
    !!canAccessTrade
  )
}

export const WebSocketBridge: React.FC = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()
  const { addSnackbar } = useSnackbar()
  const { formatMoney } = useFormatNumber()
  const { t } = useTranslation()
  const canAccessTrading = useCanAccessWebTrading()

  const handleEvent = useCallback(
    (message: WebSocketMessage) => {
      const event = normalizeTransactionWebSocketEvent(message)
      if (!event) return

      refreshTransactionEventData(queryClient, event)

      const snackbar = buildTransactionSnackbar(event, formatMoney, canAccessTrading, t)
      if (!snackbar) return

      const { action } = snackbar

      addSnackbar[snackbar.type]({
        message: snackbar.message,
        subTitle: snackbar.subTitle,
        action: action
          ? {
              label: action.label,
              onClick: () =>
                action.isFundingEntry ? navigateToFunding(action.path) : navigate(action.path),
            }
          : undefined,
      })
    },
    [addSnackbar, canAccessTrading, formatMoney, navigate, navigateToFunding, queryClient, t]
  )

  useTransactionWebSocketMessage(handleEvent)

  return null
}
